import { Button } from "@/components/ui/baseUi/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/baseUi/dialog"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { cn } from "@/modules/lib/utils"
import { type SimpleFormik } from "@/typings/formik"
import SearchEmpty from "@images/empty/search_empty.svg"
import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { WarningHexagonalIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { UserProgramDraftEntity } from "@modules/programs/entities/userProgramDraftEntity"
import { useProgramAsync, usePrograms } from "@modules/programs/hooks/usePrograms"
import { type ProgramOnOffInputKey } from "@modules/programs/types/programOnOffFormikType"
import { ProgramFilter, ProgramType } from "@modules/programs/types/programTypes"
import { HUBackgroundImageView } from "@modules/ui/components/huBackgroundImageView"
import { Check, Copy, LoaderCircle, Pencil, Search, Trash, X } from "lucide-react"
import { useMemo, useState } from "react"
import { Text } from "@/components/ui/baseUi/text"

type ProgramOnOffCopyProps = {
  formik: SimpleFormik<UserProgramDraftEntity, ProgramOnOffInputKey>
  className?: string
} & React.ComponentProps<"div">

export function ProgramOnOffCopy({ formik, className }: ProgramOnOffCopyProps) {
  const [dialogIsVisible, setDialogIsVisible] = useState(false)
  const [selectedProgramId, setSelectedProgramId] = useState<string | undefined>()
  const [searchTerms, setSearchTerms] = useState<string>("")

  const { data: programs, isLoading } = usePrograms({ programFilter: ProgramFilter.OWNED, size: 100 })
  const { data: onOffProgramToDuplicate, isLoading: loadingProgram } = useProgramAsync(selectedProgramId)

  const onOffPrograms = programs.content.filter((program) => program.type === ProgramType.ONOFF)
  const filteredOnOffPrograms = useMemo(() => {
    if (onOffPrograms) {
      return onOffPrograms.filter((program) => program.name.toLowerCase().includes(searchTerms.toLowerCase()))
    }
    return []
  }, [onOffPrograms, searchTerms])

  const toggleSelectedProgramId = (programId: string) => {
    if (programId === selectedProgramId) {
      setSelectedProgramId(undefined)
    } else {
      setSelectedProgramId(programId)
    }
  }

  const duplicateOnOffProgram = () => {
    if (onOffProgramToDuplicate) {
      formik.setFieldValue("programIdToDuplicate", onOffProgramToDuplicate.id)
      formik.setFieldValue("image", onOffProgramToDuplicate.image)
      formik.setFieldValue("name", `${onOffProgramToDuplicate.name} (${word("global.copied")})`)
      formik.setFieldValue("description", onOffProgramToDuplicate.description)
      formik.setFieldValue("duration", onOffProgramToDuplicate.duration)
      formik.setFieldValue("entranceCheckEnabled", onOffProgramToDuplicate.entranceCheckEnabled)
      formik.setFieldValue("sharedCoaches", onOffProgramToDuplicate.sharedCoaches)
    }
  }

  const onConfirmDialog = () => {
    duplicateOnOffProgram()
    setDialogIsVisible(false)
  }

  const clearSelection = () => {
    setSelectedProgramId(undefined)
    formik.setFieldValue("programIdToDuplicate", undefined)
  }

  const closeDialog = () => {
    setSearchTerms("")
    setDialogIsVisible(false)
  }

  return (
    <>
      <div className={cn("mb-4 flex flex-col gap-3", className)}>
        {/* Display duplicated program */}
        {onOffProgramToDuplicate && formik.values.programIdToDuplicate ? (
          <div className="mb-3">
            <Text fontStyle="LS">{word("program.form.on-off.duplicateFrom")}</Text>

            <div className="mt-2 flex flex-row items-center justify-between">
              <div className="flex flex-row items-center justify-start gap-2">
                {onOffProgramToDuplicate?.image?.url && (
                  <HUBackgroundImageView
                    size="size-[63px]"
                    image={{ url: onOffProgramToDuplicate?.image?.url }}
                    className="flex items-end justify-center rounded-xl bg-cover bg-repeat"
                  ></HUBackgroundImageView>
                )}

                <div className="flex flex-col">
                  <Text fontStyle="LL">{onOffProgramToDuplicate.name}</Text>
                  <Text fontStyle="BS">{word("global.program.name")}</Text>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="iconMd"
                  onClick={(e) => {
                    e.preventDefault()
                    setDialogIsVisible(true)
                  }}
                >
                  <Pencil />
                </Button>
                <Button
                  variant="outline"
                  size="iconMd"
                  onClick={(e) => {
                    e.preventDefault()
                    clearSelection()
                  }}
                >
                  <Trash />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          /* Display duplicate a program button */
          <Button
            variant="outline"
            disabled={isLoading || onOffPrograms.length === 0}
            onClick={(e) => {
              e.preventDefault()
              setDialogIsVisible(true)
            }}
            className="flex w-fit"
            isLoading={isLoading}
          >
            {isLoading ? <LoaderCircle className="animate-spin" /> : <Copy />}
            {word("program.form.on-off.duplicateAnotherProgram")}
          </Button>
        )}
      </div>

      <Dialog open={dialogIsVisible} onOpenChange={closeDialog}>
        <DialogContent className="h-162 w-162">
          <DialogHeader className="flex flex-col gap-4">
            <DialogTitle>{word("program.form.on-off.duplicateAProgram")}</DialogTitle>
            <DialogDescription className="mb-5 flex flex-row">
              <WarningHexagonalIcon className="mr-3 text-neutral-400" color="currentColor" size={24} />
              <Text fontStyle="BM" className="text-textDefault-caption">
                {word("program.form.on-off.duplicateExplanation")}
              </Text>
            </DialogDescription>
          </DialogHeader>

          <div className="mb-5 flex w-full">
            <InputGroup>
              <InputGroupInput
                placeholder={word("searchUserInput.modal.placeholder")}
                onChange={(e) => setSearchTerms(e.currentTarget.value)}
                value={searchTerms}
              />
              <InputGroupAddon align="inline-start">
                <Search />
              </InputGroupAddon>
              {searchTerms && (
                <InputGroupAddon align="inline-end">
                  <X className="size-4 cursor-pointer" onClick={() => setSearchTerms("")} />
                </InputGroupAddon>
              )}
            </InputGroup>
          </div>

          <div className="h-83 overflow-y-auto">
            {filteredOnOffPrograms.length > 0 ? (
              filteredOnOffPrograms.map((program) => (
                <div
                  key={program.id}
                  className={cn(
                    "mb-2 flex cursor-pointer flex-row items-center gap-2 rounded-xl px-2 py-2 hover:bg-neutral-100",
                    selectedProgramId === program.id && "bg-neutral-100",
                  )}
                  onClick={() => toggleSelectedProgramId(program.id)}
                >
                  <HUBackgroundImageView
                    image={program.image ? { url: program.image.url } : undefined}
                    size="size-[63px]"
                    className="flex items-end justify-center rounded-xl bg-cover bg-repeat"
                  >
                    <OnOffIcon color="currentColor" width={20} height={20} className="text-white" />
                  </HUBackgroundImageView>

                  <div className="flex flex-col">
                    <Text fontStyle="BM" className="text-textDefault-body">
                      {program.name}
                    </Text>
                    <Text fontStyle="BS" className="text-textDefault-caption">
                      {word("global.program.name")}
                    </Text>
                  </div>
                </div>
              ))
            ) : (
              <div className="mt-4 flex flex-col justify-center">
                <Text fontStyle="LL" className="flex justify-center text-center text-textDefault-body">
                  {word("program.form.on-off.duplicateProgramNotFound")}
                </Text>
                <div className="mt-4 flex items-center justify-center text-center">
                  <img src={SearchEmpty} alt="SessionEmptySVG" width={200} />
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex w-full flex-row gap-2">
            <Button variant="outline" className="grow" onClick={closeDialog} size="lg">
              {word("global.cancel")}
            </Button>

            <Button
              variant="primary"
              size="lg"
              disabled={!selectedProgramId || loadingProgram}
              onClick={onConfirmDialog}
              className="grow"
            >
              <Check />
              {word("program.form.on-off.duplicate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
